/**
 * La dirección del sitio publicado, arriba del paso 3.
 *
 * Era un campo opcional perdido en el formulario de conexión del paso 2, y de él
 * dependen en silencio tres cosas: la medición de PageSpeed, la conexión a
 * Search Console y el enlace "Dominio". Cuando faltaba, las tres fallaban por
 * separado y ninguna decía que el problema era el mismo — el mensaje mandaba al
 * paso 2 sin decir a qué campo (Eduardo, 2026-09-06: "Cargué la web del servidor
 * del paso 2 y sigue igual").
 *
 * Ahora vive donde se usa, dice qué depende de ella, y se carga sin salir de la
 * pantalla. La validación es la misma que la del servidor, importada de shared:
 * una regla escrita dos veces se separa en la primera corrección.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizarSiteUrl } from '@evo-hub/shared';
import { DetailSection } from '../ui/DetailSection.js';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { useToast } from '../ui/Toast.js';
import { useSiteConnection, useSaveSiteConnection } from '../../hooks/useSiteConnection.js';

const parrafo = {
  margin: 0,
  fontSize: 'var(--evo-fs-body)',
  color: 'var(--evo-text-muted)',
  fontFamily: 'var(--evo-font)',
  lineHeight: 1.65,
  maxWidth: '62ch',
} as const;

export function DireccionDelSitio({ projectId }: { projectId: number }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isLoading } = useSiteConnection(projectId);
  const guardar = useSaveSiteConnection(projectId);

  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');

  if (isLoading) return null;
  const conn = data?.connection ?? null;

  // Sin conexión no hay fila donde guardar la dirección: el formulario del paso 2
  // pide host y usuario, y mandar solo la dirección devuelve 400. Se dice, en vez
  // de ofrecer un campo que no va a poder guardar.
  if (!conn) {
    return (
      <DetailSection title={t('projects.design.tab.step3.siteAddress.title')} divider={false}>
        <p style={parrafo}>{t('projects.design.tab.step3.siteAddress.needsConnection')}</p>
      </DetailSection>
    );
  }

  const validacion = editando && texto.trim() !== '' ? normalizarSiteUrl(texto) : null;
  const invalida = validacion && !validacion.ok ? validacion.motivo : null;

  function abrir(): void {
    setTexto(conn?.siteUrl ?? '');
    setEditando(true);
  }

  function confirmar(): void {
    const r = normalizarSiteUrl(texto);
    if (!r.ok) return;
    // El resto de la conexión viaja igual: el servidor pide host y usuario, y
    // mandar solo la dirección sería un 400. El secreto NO viaja — omitirlo es
    // lo que le dice al servidor que conserve el guardado.
    guardar.mutate(
      {
        siteUrl: r.url,
        protocol: conn!.protocol,
        host: conn!.host,
        port: conn!.port,
        username: conn!.username,
        authMethod: conn!.authMethod,
        remotePath: conn!.remotePath,
      },
      {
        onSuccess: () => {
          setEditando(false);
          toast.success(t('projects.design.tab.step3.siteAddress.saved'));
        },
        onError: e => { toast.error(e instanceof Error ? e.message : t('common.error')); },
      },
    );
  }

  const falta = !conn.siteUrl;

  return (
    <DetailSection
      title={t('projects.design.tab.step3.siteAddress.title')}
      divider={false}
      accent={falta}
    >
      {falta && !editando
        ? (
          <>
            {/* Qué se rompe sin esto, enumerado. "Falta la dirección" a secas no
                explica por qué tres pantallas distintas no funcionan. */}
            <p style={parrafo}>{t('projects.design.tab.step3.siteAddress.missing')}</p>
            <ul style={{ ...parrafo, margin: 'var(--evo-space-2) 0 0', paddingLeft: '1.1rem' }}>
              <li>{t('projects.design.tab.step3.siteAddress.blocksSpeed')}</li>
              <li>{t('projects.design.tab.step3.siteAddress.blocksConsole')}</li>
              <li>{t('projects.design.tab.step3.siteAddress.blocksLink')}</li>
            </ul>
            <div style={{ marginTop: 'var(--evo-space-3)' }}>
              <Button size="sm" tone="cyan" onClick={abrir}>
                {t('projects.design.tab.step3.siteAddress.add')}
              </Button>
            </div>
          </>
        )
        : editando
          ? (
            <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Input
                  value={texto}
                  onChange={setTexto}
                  size="sm"
                  fullWidth
                  autoFocus
                  placeholder="https://ejemplo.com"
                  {...(invalida ? { error: t(`projects.design.tab.step3.siteAddress.invalid.${invalida}`) } : {})}
                />
              </div>
              <Button
                size="sm"
                tone="cyan"
                disabled={!normalizarSiteUrl(texto).ok}
                loading={guardar.isPending}
                onClick={confirmar}
              >
                {t('common.save')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setEditando(false); }}>
                {t('common.cancel')}
              </Button>
            </div>
          )
          : (
            <div style={{ display: 'flex', gap: 'var(--evo-space-3)', alignItems: 'baseline', flexWrap: 'wrap' }}>
              <a
                href={conn.siteUrl!}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-cyan)', textDecoration: 'none', fontFamily: 'var(--evo-font)' }}
              >
                {conn.siteUrl!.replace(/^https?:\/\//, '')}
              </a>
              <Button size="xs" variant="outline" onClick={abrir}>
                {t('common.edit')}
              </Button>
            </div>
          )}
    </DetailSection>
  );
}
